import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthSearchService, GrowthSxoExperiment } from './growth-search.service';
import { GrowthSeoProfileSelectorComponent } from '../../shared/design-system/growth-seo-profile-selector.component';

interface VariantModel {
  headline: string;
  ctaText: string;
  body: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-sxo-experiments',
  imports: [AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, GrowthSeoProfileSelectorComponent, FormsModule, TableModule, ButtonModule, DialogModule, InputTextModule, TextareaModule],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.search.sxo.title')" [description]="t('growth.search.sxo.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.search.sxo.hint.title'" [descriptionKey]="'growth.search.sxo.hint.description'" [bulletKeys]="['growth.search.sxo.hint.bullets.experiment', 'growth.search.sxo.hint.bullets.complete']" />
      <section class="card">
        <div class="toolbar"><button pButton type="button" [label]="t('growth.search.sxo.actions.newExperiment')" (click)="openDialog()"></button><button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button></div>
        @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
          <p-table [value]="experiments()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header"><tr><th>Name</th><th>Status</th><th>Seo Profile</th><th>Actions</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.name }}</td><td>{{ row.status }}</td><td>{{ row.seoProfileId ?? '-' }}</td><td class="actions"><button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="edit(row)"></button><button pButton type="button" size="small" [label]="t('growth.search.sxo.actions.complete')" [disabled]="row.status === 'Completed'" (click)="complete(row)"></button></td></tr></ng-template>
          </p-table>
        }
      </section>
      <p-dialog [header]="t('growth.search.sxo.dialog.title')" [visible]="dialogVisible()" (visibleChange)="dialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(860px,95vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.search.sxo.fields.name')"><input pInputText [(ngModel)]="model.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.sxo.fields.seoProfile')"><ain-growth-seo-profile-selector [(ngModel)]="model.seoProfileId" /></ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.search.sxo.sections.variantA') }}</h4></div>
        <div class="grid">
          <ain-form-field [label]="t('growth.search.sxo.fields.variantHeadline')"><input pInputText [(ngModel)]="variantA.headline" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.sxo.fields.variantCtaText')"><input pInputText [(ngModel)]="variantA.ctaText" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.sxo.fields.variantBody')"><textarea pInputTextarea rows="3" [(ngModel)]="variantA.body"></textarea></ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.search.sxo.sections.variantB') }}</h4></div>
        <div class="grid">
          <ain-form-field [label]="t('growth.search.sxo.fields.variantHeadline')"><input pInputText [(ngModel)]="variantB.headline" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.sxo.fields.variantCtaText')"><input pInputText [(ngModel)]="variantB.ctaText" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.sxo.fields.variantBody')"><textarea pInputTextarea rows="3" [(ngModel)]="variantB.body"></textarea></ain-form-field>
        </div>
        <ng-template pTemplate="footer"><button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button><button pButton type="button" [label]="t('common.save')" (click)="save()"></button></ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`.card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; } .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); } .toolbar,.actions { display: flex; gap: 8px; flex-wrap: wrap; } .section-title { margin-top: 8px; }`]
})
export class GrowthSxoExperimentsComponent implements OnInit {
  private readonly service = inject(GrowthSearchService);
  private readonly i18n = inject(LocalizationService);
  readonly loading = signal(false);
  readonly experiments = signal<GrowthSxoExperiment[]>([]);
  readonly dialogVisible = signal(false);
  selectedId: string | null = null;
  model = { name: '', seoProfileId: null as string | null };
  variantA: VariantModel = { headline: 'Variant A', ctaText: '', body: '' };
  variantB: VariantModel = { headline: 'Variant B', ctaText: '', body: '' };
  ngOnInit(): void { this.load(); }
  load(): void { this.loading.set(true); this.service.listSxoExperiments().subscribe({ next: rows => { this.experiments.set(rows); this.loading.set(false); }, error: () => this.loading.set(false) }); }
  openDialog(): void {
    this.selectedId = null;
    this.model = { name: '', seoProfileId: null };
    this.variantA = { headline: 'Variant A', ctaText: '', body: '' };
    this.variantB = { headline: 'Variant B', ctaText: '', body: '' };
    this.dialogVisible.set(true);
  }
  edit(row: GrowthSxoExperiment): void {
    this.selectedId = row.id;
    this.model = { name: row.name, seoProfileId: row.seoProfileId };
    this.variantA = this.parseVariant(row.variantAJson, 'Variant A');
    this.variantB = this.parseVariant(row.variantBJson, 'Variant B');
    this.dialogVisible.set(true);
  }
  save(): void {
    this.service.upsertSxoExperiment({
      id: this.selectedId,
      name: this.model.name,
      seoProfileId: this.model.seoProfileId,
      variantAJson: JSON.stringify(this.variantA),
      variantBJson: JSON.stringify(this.variantB)
    }).subscribe({ next: () => { this.dialogVisible.set(false); this.load(); } });
  }
  complete(row: GrowthSxoExperiment): void { this.service.completeSxoExperiment(row.id, '{"winner":"A","confidence":0.71}').subscribe({ next: () => this.load() }); }
  private parseVariant(json: string, defaultHeadline: string): VariantModel {
    try {
      const parsed = JSON.parse(json) as Partial<VariantModel>;
      return {
        headline: parsed.headline?.toString() || defaultHeadline,
        ctaText: parsed.ctaText?.toString() || '',
        body: parsed.body?.toString() || ''
      };
    } catch {
      return { headline: defaultHeadline, ctaText: '', body: '' };
    }
  }
  t(key: string): string { return this.i18n.translate(key); }
}
